import { Link } from "react-router-dom";
import { MapPin, Mail, Phone } from "lucide-react";
import { ASSETS, ORG, NAV_LINKS } from "@/lib/constants";
import { SocialIcons } from "@/components/SocialIcons";

const FOOTER_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/#about" },
  { label: "Programs", to: "/#programs" },
  { label: "AAU", to: "/aau-tryouts" },
  { label: "Training", to: "/private-training" },
  { label: "Gallery", to: "/gallery" },
  { label: "Merch", to: "/merch" },
  { label: "Staff", to: "/#staff" },
  { label: "Contact", to: "/#contact" },
];

export function Footer() {
  return (
    <footer data-testid="footer" className="relative bg-ink-900 border-t border-white/10 overflow-hidden">
      <div className="absolute inset-0 diag-stripe opacity-40" />
      <div className="container-x relative py-14">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img src={ASSETS.logo} alt="CT Blazers logo" className="h-14 w-auto" />
              <span className="font-heading text-3xl tracking-wide text-white">
                CT <span className="text-blaze-orange">BLAZERS</span>
              </span>
            </div>
            <p className="mt-5 text-white/60 max-w-sm leading-relaxed">
              Developing players. Building character. Playing the right way.
            </p>
            <SocialIcons className="mt-6" />
          </div>

          <div className="md:col-span-3">
            <h4 className="font-heading text-xl tracking-wide text-white mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {FOOTER_LINKS.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    data-testid={`footer-${l.label.toLowerCase()}`}
                    className="text-sm text-white/60 hover:text-blaze-orange transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-heading text-xl tracking-wide text-white mb-4">Get In Touch</h4>
            <ul className="space-y-3 text-sm text-white/60">
              <li className="flex items-center gap-3">
                <MapPin size={16} className="text-blaze-blueLight" /> {ORG.city}
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-blaze-blueLight" />
                <a href={`mailto:${ORG.email}`} className="hover:text-blaze-orange transition-colors break-all">
                  {ORG.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-blaze-blueLight" />
                <a href="tel:+12034148688" className="hover:text-blaze-orange transition-colors">
                  (203) 414-8688
                </a>
              </li>
            </ul>
            <Link
              to="/admin/login"
              data-testid="footer-admin-link"
              className="inline-block mt-6 text-xs text-white/45 hover:text-white/80 transition-colors"
            >
              Staff / Admin Login
            </Link>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/40">© 2026 CT BLAZERS. All Rights Reserved.</p>
          <p className="text-xs text-white/40 uppercase tracking-widest">Competitive · Family-Friendly · Development-Focused</p>
        </div>

        <div className="mt-6 text-center">
          <p data-testid="footer-credit" className="text-xs text-white/30">
            Created by{" "}
            <a
              href="https://omraowebstudio.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-credit-link"
              className="text-white/45 hover:text-blaze-orange transition-colors"
            >
              OMRAO Web Studio
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
